import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.js';
import Alert from '../components/common/Alert.js';
import {
  canCreateMlSheet,
  canManualAttendance,
  hasAttendanceAccess,
  hasLocationHubBookingAccess,
  hubAllowedPaymentModes,
} from '../utils/networkMode.js';
import {
  canUseDeliverShortcut,
  canUseShipmentSearchShortcut,
} from '../utils/appShortcuts.js';
import {
  eventLetter,
  isAltKey,
  isAppModalOpen,
  isModKey,
  isTypingTarget,
} from '../utils/keyboard.js';
import { navGoPathByKey, NAV_GO_PREFIX } from '../utils/sidebarMenu.js';

export default function useAppHotkeys({
  onModX,
  onToggleHelp,
  onFocusSearch,
  onToggleAttendance,
  onOpenJumpPalette,
} = {}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const pendingGo = useRef(false);
  const goTimer = useRef(null);

  useEffect(() => {
    if (!user) return undefined;

    const clearGo = () => {
      pendingGo.current = false;
      if (goTimer.current) {
        window.clearTimeout(goTimer.current);
        goTimer.current = null;
      }
    };

    const goMap = navGoPathByKey(user);

    const onKeyDown = (event) => {
      const key = event.key;
      const typing = isTypingTarget(event.target);
      const letter = eventLetter(event);
      const helpOpen = Boolean(document.querySelector('[data-shortcuts-help="true"]'));
      const jumpOpen = Boolean(document.querySelector('[data-nav-jump="true"]'));

      if (key === 'Escape' && helpOpen) {
        event.preventDefault();
        onToggleHelp?.(false);
        return;
      }

      if (!typing && (key === '?' || (event.shiftKey && key === '/'))) {
        event.preventDefault();
        onToggleHelp?.();
        return;
      }

      if (helpOpen || jumpOpen) return;

      if (pendingGo.current) {
        if (typing) {
          clearGo();
        } else {
          event.preventDefault();
          const chord = key === '.' ? '.' : letter;
          clearGo();
          if (key === 'Escape') return;
          if (key === ' ' || key === 'Spacebar') {
            onOpenJumpPalette?.();
            return;
          }
          const path = goMap[chord];
          if (path) navigate(path);
          return;
        }
      }

      if (isModKey(event) && event.shiftKey && letter === 'l' && !event.altKey) {
        if (typing) return;
        if (!hasAttendanceAccess(user) || !canManualAttendance(user)) return;
        event.preventDefault();
        onToggleAttendance?.();
        return;
      }

      if (isModKey(event) && !event.altKey && !event.shiftKey && letter === 'x') {
        if (typing) return;
        if (!canUseDeliverShortcut(user)) return;
        event.preventDefault();
        onModX?.({
          pathname: location.pathname,
          search: location.search,
        });
        return;
      }

      if (isModKey(event) && !event.altKey && !event.shiftKey && letter === 'd') {
        if (typing) return;
        if (!canCreateMlSheet(user)) return;
        event.preventDefault();
        navigate('/ml-sheets/add');
        return;
      }

      if (typing || isAppModalOpen()) return;

      if (
        letter === NAV_GO_PREFIX
        && !isModKey(event)
        && !event.altKey
        && !event.shiftKey
      ) {
        event.preventDefault();
        pendingGo.current = true;
        goTimer.current = window.setTimeout(clearGo, 1200);
        return;
      }

      if (key === '/' && !event.altKey && !isModKey(event) && !event.shiftKey) {
        if (!canUseShipmentSearchShortcut(user)) return;
        event.preventDefault();
        onFocusSearch?.();
        return;
      }

      if (!hasLocationHubBookingAccess(user)) return;

      const allowed = hubAllowedPaymentModes(user);
      const goAdd = (mode, manual = false) => {
        if (mode && !allowed.includes(mode)) {
          const labels = { paid: 'Paid', to_pay: 'To Pay', credit: 'Credit' };
          Alert.error(`${labels[mode] || mode} is not allowed at this branch`);
          return;
        }
        const params = new URLSearchParams();
        if (mode) params.set('mode', mode);
        if (manual) params.set('manual', '1');
        navigate(`/shipments/add?${params.toString()}`);
      };

      if (key === 'F7') {
        event.preventDefault();
        goAdd('paid');
        return;
      }
      if (key === 'F8') {
        event.preventDefault();
        goAdd('to_pay');
        return;
      }
      if (key === 'F9') {
        event.preventDefault();
        goAdd('credit');
        return;
      }
      if (isAltKey(event) && letter === 'm') {
        event.preventDefault();
        goAdd(null, true);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      clearGo();
    };
  }, [
    user,
    navigate,
    location.pathname,
    location.search,
    onModX,
    onToggleHelp,
    onFocusSearch,
    onToggleAttendance,
    onOpenJumpPalette,
  ]);
}
